import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { signOut } from '../../../services/cognito';
import { landlordApi } from '../../../services/api';
import { featureFlags } from '../../../utils/featureFlags';
import { clearSessionPreservingPreferences } from '../../../utils/sessionStorage';
import { getAppMarket } from '../../../services/MarketService';
import JurisdictionSwitcher from '../../JurisdictionSwitcher';
import { TexasGroupedSidebar } from '../../../nav/TexasGroupedSidebar';

interface MarketAwareNavbarProps {
    expiringSoon?: number;
    overdue?: number;
}

// F-308: the UK-only nav list (EPC/HMO/Gas Safety/Awaab) was dead code here —
// the UK menu is built inline below and this market never renders it.
const MarketAwareNavbar = ({ expiringSoon = 0, overdue = 0 }: MarketAwareNavbarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [currentMarket, setCurrentMarket] = useState<string>(getAppMarket());

    // Listen for market changes (when user switches markets via JurisdictionSwitcher)
    useEffect(() => {
        const handleMarketChange = () => {
            setCurrentMarket(getAppMarket());
        };

        window.addEventListener('marketChange', handleMarketChange);
        return () => window.removeEventListener('marketChange', handleMarketChange);
    }, []);

    const [isTrial, setIsTrial] = useState(false);
    const [isAdmin] = useState(() => localStorage.getItem('isAdmin') === 'true');
    const [isSuperAdmin] = useState(() => localStorage.getItem('isSuperAdmin') === 'true');

    useEffect(() => {
        let cancelled = false;
        landlordApi.get('api/subscription')
            .then((res) => {
                const plan = (res?.data?.data ?? res?.data ?? {}) as any;
                const trialEnd = plan?.trialEndDate;
                // If backend says trial and no local marker yet, set it
                if (trialEnd && !localStorage.getItem('subscriptionStatus')) {
                    try { localStorage.setItem('subscriptionStatus', 'trial'); } catch {}
                }
                setIsTrial(!!trialEnd);
            })
            .catch(() => {
                // Fall back to localStorage on network error
                setIsTrial(localStorage.getItem('subscriptionStatus') === 'trial');
            });
        return () => { cancelled = true; };
    }, []);
    const isPremiumLocked = isTrial && !isAdmin && !isSuperAdmin;

    const PREMIUM_FEATURES = ['evictions', 'agreements', 'aml', 'finance', 'arrears', 'analysis', 'communications', 'repairs-tracking', 'awaab-law'];

    const handlePremiumClick = (e: React.MouseEvent, feature: string) => {
        if (isPremiumLocked && PREMIUM_FEATURES.includes(feature)) {
            e.preventDefault();
            navigate('/pricing?upgrade=1&feature=' + feature);
        }
    };

    const LockBadge = () => (
        <span
            title="Upgrade to unlock"
            style={{ fontSize: '11px', marginLeft: '4px', opacity: 0.7 }}
        >
            🔒
        </span>
    );

    const handleLogout = () => {
        signOut();
        clearSessionPreservingPreferences();
        window.location.href = '/';
    };

    const handleAddProperty = () => {
        navigate('/add-property');
    };

    const isActive = (path: string) => {
        if (path === '/dashboard') {
            return location.pathname === '/dashboard';
        }
        return location.pathname.startsWith(path);
    };

    const getLinkStyles = (path: string) => {
        const active = isActive(path);
        return active
            ? "text-green-600 font-semibold"
            : "text-gray-700 hover:text-green-600 transition-colors";
    };

    const totalAlerts = expiringSoon + overdue;

    const isTexasMarket = currentMarket === 'texas';
    return (
        <nav className="bg-white shadow-lg sticky top-0 z-50" role="navigation" aria-label="Main navigation">
            <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-green-600 focus:text-white focus:px-4 focus:py-2 focus:rounded">Skip to main content</a>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    <div className="flex items-center space-x-4">
                        {/* Logo removed as per user request */}
                    </div>

                    <div className="hidden md:flex items-center space-x-8">
                        {featureFlags.DASHBOARD_ENABLED && (
                            <Link to="/dashboard" className={getLinkStyles('/dashboard')}>Dashboard</Link>
                        )}

                        {featureFlags.PROPERTIES_ENABLED && <Link to="/properties" className={getLinkStyles('/properties')}>Properties</Link>}

                        {isTexasMarket ? (
                            <TexasGroupedSidebar />
                        ) : (
                            // UK MARKET NAVIGATION
                            <div className="relative group">
                                <button
                                    className={`flex items-center space-x-1 ${isActive('/tenants') || isActive('/agreements') || isActive('/arrears') || isActive('/aml') ? 'text-green-600 font-bold' : 'text-gray-700 hover:text-green-600'} transition-all`}
                                    onClick={() => setOpenDropdown(openDropdown === 'tenants' ? null : 'tenants')}
                                >
                                    <i className="fas fa-users mr-1"></i>
                                    <span>Tenants</span>
                                    <i className="fas fa-chevron-down text-[10px] ml-1 opacity-50 group-hover:rotate-180 transition-transform"></i>
                                </button>

                                <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-100 rounded-xl shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 transform translate-y-2 group-hover:translate-y-0">
                                    <Link to="/tenants" className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium">
                                        <i className="fas fa-list-ul mr-2 w-4"></i> Tenant List
                                    </Link>
                                    {featureFlags.ARREARS_ENABLED && (
                                        <Link
                                            to="/arrears"
                                            className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                            onClick={(e) => handlePremiumClick(e, 'arrears')}
                                        >
                                            <span>
                                                <i className="fas fa-money-bill-wave mr-2 w-4"></i> Arrears
                                            </span>
                                            {isPremiumLocked && <LockBadge />}
                                        </Link>
                                    )}
                                    <Link
                                        to="/agreements"
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                        onClick={(e) => handlePremiumClick(e, 'agreements')}
                                    >
                                        <i className="fas fa-file-contract mr-2 w-4"></i> Agreements
                                        {isPremiumLocked && <LockBadge />}
                                    </Link>
                                    <Link
                                        to="/evictions"
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                        onClick={(e) => handlePremiumClick(e, 'evictions')}
                                    >
                                        <i className="fas fa-gavel mr-2 w-4"></i> Eviction Workflow
                                        {isPremiumLocked && <LockBadge />}
                                    </Link>
                                    <Link
                                        to="/aml"
                                        className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                        onClick={(e) => handlePremiumClick(e, 'aml')}
                                    >
                                        <span>
                                            <i className="fas fa-fingerprint mr-2 w-4"></i> AML Checks
                                        </span>
                                        {isPremiumLocked && <LockBadge />}
                                    </Link>
                                </div>
                            </div>
                        )}

                         {!isTexasMarket && featureFlags.CERTIFICATES_ENABLED && (
                            <Link to="/certificates" className={getLinkStyles('/certificates')}>
                                <i className="fas fa-certificate mr-1"></i>Certificates
                            </Link>
                        )}

                        {!isTexasMarket && (
                        <div className="relative group">
                            <button
                                className={`flex items-center space-x-1 ${isActive('/repairs-tracking') || isActive('/case-linking') ? 'text-green-600 font-bold' : 'text-gray-700 hover:text-green-600'} transition-all`}
                                onClick={() => setOpenDropdown(openDropdown === 'repairs' ? null : 'repairs')}
                            >
                                <i className="fas fa-toolbox mr-1"></i>
                                <span>Repairs Tracking</span>
                                {isPremiumLocked && <LockBadge />}
                                <i className="fas fa-chevron-down text-[10px] ml-1 opacity-50 group-hover:rotate-180 transition-transform"></i>
                            </button>
                            <div className="absolute left-0 mt-2 w-52 bg-white border border-gray-100 rounded-xl shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 transform translate-y-2 group-hover:translate-y-0">
                                <Link
                                    to="/repairs-tracking"
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                    onClick={(e) => handlePremiumClick(e, 'repairs-tracking')}
                                >
                                    <i className="fas fa-toolbox mr-2 w-4"></i> Repairs Queue
                                </Link>
                                <Link
                                    to="/case-linking"
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 font-medium"
                                    onClick={(e) => handlePremiumClick(e, 'repairs-tracking')}
                                >
                                    <i className="fas fa-link mr-2 w-4"></i> Case Linking
                                </Link>
                            </div>
                        </div>
                        )}

                        <Link
                            to="/legal-qa"
                            className={getLinkStyles('/legal-qa')}
                            onClick={(e) => handlePremiumClick(e, 'analysis')}
                        >
                            <i className="fas fa-gavel mr-1"></i>Compliance Advisor
                            {isPremiumLocked && <LockBadge />}
                        </Link>

                        {featureFlags.TEAM_ENABLED && <Link to="/team" className={getLinkStyles('/team')}>Team</Link>}

                        {(() => {
                            const isAdminFlag      = localStorage.getItem('isAdmin') === 'true';
                            const isSuperAdminFlag = localStorage.getItem('isSuperAdmin') === 'true';
                            const role             = (localStorage.getItem('userRole') || '').toUpperCase();
                            const isAdminRole      = ['ADMIN', 'TENANT_ADMIN', 'TENANT ADMIN', 'SUPER_ADMIN', 'SUPER ADMIN'].includes(role);
                            const showBranding     = isAdminFlag || isSuperAdminFlag || isAdminRole;
                             if (!showBranding) return null;
                            return (
                                <>
                                    <a
                                        href="/settings?tab=branding"
                                        className="text-gray-700 hover:text-green-600 transition-colors"
                                    >
                                        <i className="fas fa-palette mr-1"></i>Branding
                                    </a>
                                    {isAdminFlag && (
                                        <a
                                            href={isSuperAdminFlag ? "/super-admin" : "/admin-portal"}
                                            className="text-gray-700 hover:text-green-600 transition-colors"
                                        >
                                            <i className="fas fa-user-shield mr-1"></i>
                                            {isSuperAdminFlag ? 'Super Admin' : 'Admin Portal'}
                                        </a>
                                    )}
                                </>
                            );
                        })()}

                        <div className="flex items-center space-x-4">
                            <JurisdictionSwitcher compact />
                            {!isTexasMarket && location.pathname === '/properties' && (
                                <button
                                    onClick={handleAddProperty}
                                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                                >
                                    <i className="fas fa-plus mr-2"></i>Add Property
                                </button>
                            )}
                            <button
                                onClick={handleLogout}
                                className="text-gray-500 hover:text-red-600 transition-colors font-medium"
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>

                    <div className="flex md:hidden items-center space-x-3">
                        <JurisdictionSwitcher compact />
                        {totalAlerts > 0 && (
                            <Link
                                to="/dashboard"
                                className="relative bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-sm font-medium flex items-center"
                            >
                                <i className="fas fa-exclamation-triangle mr-1.5"></i>
                                {totalAlerts}
                            </Link>
                        )}
                        {!isTexasMarket && location.pathname === '/properties' && (
                            <button
                                onClick={handleAddProperty}
                                className="bg-green-600 text-white w-9 h-9 rounded-full flex items-center justify-center shadow-sm hover:bg-green-700 transition-colors"
                                aria-label="Add Property"
                            >
                                <i className="fas fa-plus text-sm"></i>
                            </button>
                        )}
                        <button
                            onClick={handleLogout}
                            className="text-gray-500 hover:text-red-600 p-2"
                            aria-label="Sign Out"
                        >
                            <i className="fas fa-sign-out-alt text-lg"></i>
                        </button>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default MarketAwareNavbar;
